import React, { useState } from 'react';
import { Quest } from '../types';
import { useLanguage } from '../contexts/LanguageContext';

interface QuestRescheduleModalProps {
  quest: Quest;
  isOpen: boolean;
  onClose: () => void;
  onReschedule: (questId: string, newDate: string) => void;
}

const QuestRescheduleModal: React.FC<QuestRescheduleModalProps> = ({
  quest,
  isOpen,
  onClose,
  onReschedule,
}) => {
  const { t } = useLanguage();
  const [selectedDate, setSelectedDate] = useState('');
  const [isRescheduling, setIsRescheduling] = useState(false);

  // Get today's date in YYYY-MM-DD format
  const today = new Date().toISOString().split('T')[0];
  
  // Get date 30 days from now for max date
  const maxDate = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

  const handleReschedule = async () => {
    if (!selectedDate) return;
    
    setIsRescheduling(true);
    try {
      await onReschedule(quest.id, selectedDate);
      onClose();
    } catch (error) {
      console.error('Error rescheduling quest:', error);
    } finally {
      setIsRescheduling(false);
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        {/* Modal */}
        <div 
          className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 border-2 border-system-blue/50 rounded-lg shadow-2xl max-w-md w-full relative overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Animated Background */}
          <div className="absolute inset-0 bg-gradient-to-br from-blue-900/20 via-purple-900/20 to-blue-900/20 animate-pulse" />
          
          {/* Content */}
          <div className="relative z-10 p-6">
            {/* Header */}
            <div className="text-center mb-6">
              <div className="text-4xl mb-2">📅</div>
              <h2 className="text-xl font-orbitron font-bold text-white mb-2">
                Reschedule Quest
              </h2>
              <p className="text-sm text-gray-400 font-rajdhani">
                Choose a new date for this quest
              </p>
            </div>

            {/* Quest Info */}
            <div className="bg-black/30 border border-system-blue/30 rounded-lg p-4 mb-6">
              <div className="font-orbitron font-bold text-white text-sm mb-1">
                {quest.title}
              </div>
              {quest.description && (
                <div className="text-xs text-gray-400 font-rajdhani">
                  {quest.description}
                </div>
              )}
            </div>

            {/* Date Selection */}
            <div className="mb-6">
              <label className="block text-sm font-orbitron font-bold text-white mb-2">
                Select New Date
              </label>
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                min={today}
                max={maxDate}
                className="w-full px-3 py-2 bg-black/50 border border-system-blue/50 rounded text-white font-rajdhani focus:border-system-blue focus:outline-none"
              />
              {selectedDate && (
                <div className="mt-2 text-xs text-gray-400 font-rajdhani">
                  Selected: {formatDate(selectedDate)}
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="flex-1 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white font-orbitron font-bold text-xs tracking-wide uppercase rounded transition-all"
              >
                Cancel
              </button>
              <button
                onClick={handleReschedule}
                disabled={!selectedDate || isRescheduling}
                className={`flex-1 px-4 py-2 font-orbitron font-bold text-xs tracking-wide uppercase rounded transition-all ${
                  selectedDate && !isRescheduling
                    ? 'bg-gradient-to-r from-system-blue to-blue-600 hover:from-system-blue/80 hover:to-blue-600/80 text-white shadow-[0_0_20px_rgba(59,130,246,0.5)]'
                    : 'bg-gray-600 text-gray-400 cursor-not-allowed'
                }`}
              >
                {isRescheduling ? 'Rescheduling...' : 'Reschedule'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default QuestRescheduleModal;
